import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * <SEO>  — Iter 39
 * Reusable head-meta helper. Every public route should render one to make
 * BookTalent search-engine friendly. Falls back to sensible defaults so
 * unrouted pages never leak an empty <title> or missing OG image.
 */
const SITE_NAME = "BookTalent";
const SITE_URL = "https://booktalent.in";
const DEFAULT_TITLE = "BookTalent — Book India's Finest Talent";
const DEFAULT_DESC =
  "BookTalent — a talent marketplace for booking verified singers, DJs, comedians, dancers and anchors for weddings, corporate events, concerts and private parties.";
const DEFAULT_IMAGE = `${SITE_URL}/og-cover.png`;

const PAGE_SEO = {
  "/": ["Book India's Finest Talent, On Demand", DEFAULT_DESC],
  "/login": ["Sign In", "Sign in to your BookTalent account to manage bookings, discover artists, or access your talent dashboard."],
  "/signup": ["Create Your Account", "Join BookTalent to discover performers, book events, or grow your career as an artist."],
  "/forgot-password": ["Reset Your Password", "Request a secure password reset link for your BookTalent account."],
  "/reset-password": ["Choose a New Password", "Set a new password for your BookTalent account."],
  "/manager": ["Manager Dashboard", "Manage your BookTalent leads, artist relationships, bookings, and performance."],
  "/manager/leaderboard": ["Manager Leaderboard", "View manager performance and leaderboard results on BookTalent."],
  "/manager/chat": ["Manager Messages", "Manage conversations and follow up with BookTalent leads."],
  "/manager/leads": ["Manager Leads", "Review and manage your BookTalent customer and booking leads."],
  "/trust": ["Trust & Safety", "Learn how BookTalent supports safer, more reliable bookings between customers and performers."],
  "/settings/notifications": ["Notification Preferences", "Manage the email and in-app notifications for your BookTalent account."],
  "/search": ["Find Artists for Your Event", "Search and compare verified performers by category, city, budget, availability, and event type."],
  "/discover": ["Discover Artists", "Discover performers for weddings, corporate events, celebrations, and private parties across India."],
  "/help": ["Help Centre", "Find answers and support for discovering artists, managing bookings, payments, and your BookTalent account."],
  "/blog": ["Talent, Events & Entertainment Blog", "Ideas, expert advice, and updates for booking performers and planning memorable events in India."],
  "/planner": ["Plan Your Event", "Plan your event with BookTalent and find the performers, services, and details you need."],
  "/booking/payment-return": ["Payment Status", "Check the status of your BookTalent booking payment."],
  "/customer": ["Customer Dashboard", "Manage your BookTalent bookings, saved artists, conversations, and account details."],
  "/artist": ["Artist Dashboard", "Manage your BookTalent artist profile, packages, availability, enquiries, and bookings."],
  "/corporate": ["Corporate Dashboard", "Manage corporate event enquiries, artist bookings, budgets, and company details on BookTalent."],
  "/admin": ["Admin Dashboard", "Manage BookTalent artists, KYC reviews, bookings, and platform operations."],
  "/onboarding": ["Onboarding Dashboard", "Track the artists you have onboarded to BookTalent."],
  "/404": ["Page Not Found", "The page you are looking for could not be found on BookTalent."],
};

const PRIVATE_ROUTES = [
  "/login", "/signup", "/forgot-password", "/reset-password",
  "/manager", "/settings/notifications", "/booking/payment-return",
  "/customer", "/artist", "/agency", "/corporate", "/admin", "/book", "/bookings",
  "/onboarding", "/staff",
];

function titleFromSlug(slug) {
  return decodeURIComponent(slug || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function routeMetadata(pathname) {
  const exact = PAGE_SEO[pathname];
  if (exact) return { title: exact[0], description: exact[1] };

  let match = pathname.match(/^\/manager\/leads\/[^/]+$/);
  if (match) {
    return {
      title: "Lead Details",
      description: "Review lead information, activity, and follow-up details in the BookTalent manager workspace.",
    };
  }
  match = pathname.match(/^\/artist\/([^/]+)$/);
  if (match) {
    return {
      title: `${titleFromSlug(match[1])} — Artist Profile`,
      description: "Explore this performer’s profile, performance packages, media, and booking information on BookTalent.",
    };
  }
  match = pathname.match(/^\/artists\/city\/([^/]+)$/);
  if (match) {
    const city = titleFromSlug(match[1]);
    return {
      title: `Artists for Events in ${city}`,
      description: `Discover and book singers, DJs, performers, and event artists in ${city} with BookTalent.`,
    };
  }
  match = pathname.match(/^\/artists\/([^/]+)$/);
  if (match) {
    const category = titleFromSlug(match[1]);
    return {
      title: `${category} for Events`,
      description: `Discover and book ${category.toLowerCase()} for weddings, corporate events, and celebrations across India.`,
    };
  }
  match = pathname.match(/^\/blog\/([^/]+)$/);
  if (match) {
    return {
      title: titleFromSlug(match[1]),
      description: "Read event-planning ideas, performer stories, and entertainment advice from the BookTalent blog.",
    };
  }
  if (/^\/page\/[^/]+$/.test(pathname)) {
    return {
      title: "BookTalent",
      description: "Learn more about BookTalent, the marketplace for discovering and booking performers across India.",
    };
  }
  if (/^\/recap\/[^/]+$/.test(pathname)) {
    return {
      title: "Event Recap",
      description: "See highlights from an event booked through BookTalent.",
    };
  }
  if (/^\/book\/[^/]+$/.test(pathname)) {
    return {
      title: "Complete Your Booking",
      description: "Review event details and complete your artist booking securely through BookTalent.",
    };
  }
  if (/^\/bookings\/[^/]+$/.test(pathname)) {
    return {
      title: "Booking Details",
      description: "Review the status, schedule, and details of your BookTalent booking.",
    };
  }
  if (/^\/staff\/artists\/[^/]+$/.test(pathname)) {
    return { title: "Artist Review", description: "Staff view of an artist's profile and onboarding progress.", noindex: true };
  }
  if (pathname.startsWith("/agency")) {
    const section = pathname.split("/").filter(Boolean)[1];
    const sectionTitle = section ? titleFromSlug(section) : "Overview";
    return {
      title: `Agency ${sectionTitle}`,
      description: `Manage agency ${sectionTitle.toLowerCase()}, artists, clients, bookings, and operations in BookTalent.`,
    };
  }
  return { title: PAGE_SEO["/404"][0], description: PAGE_SEO["/404"][1], noindex: true };
}

export function RouteSEO() {
  const { pathname } = useLocation();
  const { title, description, noindex = false } = routeMetadata(pathname);
  const isPrivate = PRIVATE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  const fullTitle = `${title} · ${SITE_NAME}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex || isPrivate ? "noindex, nofollow" : "index, follow"} />
    </Helmet>
  );
}

export default function SEO({
  title,
  description,
  keywords,
  image,
  canonical,
  path,           // path relative to site root, e.g. "/artist/priya-sharma-…"
  noindex = false,
  jsonLd,         // object or array of JSON-LD objects
}) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const img = image || DEFAULT_IMAGE;
  // For dev previews we can't reliably know the public host — construct a
  // sensible canonical from the current location when we're not given one.
  const url =
    canonical ||
    (path
      ? `${SITE_URL}${path}`
      : typeof window !== "undefined"
      ? window.location.origin + window.location.pathname
      : SITE_URL);
  const ldItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Organization JSON-LD (repeated on every page for consistent E-E-A-T) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          logo: `${SITE_URL}/logo.png`,
          sameAs: [
            "https://www.instagram.com/booktalent",
            "https://www.linkedin.com/company/booktalent",
          ],
        })}
      </script>

      {ldItems.map((obj, i) => (
        <script key={`ld-${obj?.["@type"] || "obj"}-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * BreadcrumbList helper. Pass an array of `{name, url}` items — first should
 * always be Home.
 */
export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
}
